import { ReactNode } from 'react';
import { Box } from '@spark-web/box';
import { ExclamationIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextList } from '@spark-web/text-list';

type IncomeVerificationErrorPanelProps = {
  title: string;
  description?: ReactNode;
  descriptionItems?: ReactNode[];
  callout?: ReactNode;
  guidanceTitle?: string;
  guidanceItems?: string[];
};

export function IncomeVerificationErrorPanel({
  title,
  description,
  descriptionItems = [],
  callout,
  guidanceTitle,
  guidanceItems = [],
}: IncomeVerificationErrorPanelProps) {
  return (
    <Box>
      <Stack gap="large">
        {/* Icon is 32px on mobile only; desktop keeps the larger 56px. */}
        <Box display={{ mobile: 'block', tablet: 'none' }}>
          <ExclamationIcon size="small" tone="caution" />
        </Box>
        <Box display={{ mobile: 'none', tablet: 'block' }}>
          <ExclamationIcon size="large" tone="caution" />
        </Box>
        {/* Title→description gap is 24px on desktop, 16px on mobile.
            The extra 16px below the block is only for the callout/guidance panels that follow it —
            the enclosing Stack's own 16px gap already separates the text from whatever comes next,
            so applying it unconditionally doubled the space above the assistant hand-off link. */}
        <Stack
          gap={{ mobile: 'large', tablet: 'xlarge' }}
          marginBottom={callout || guidanceTitle || guidanceItems.length ? 'large' : 'none'}
        >
          {/* Heading is 19px on mobile only; desktop keeps 24px. (Text size isn't responsive.) */}
          <Box display={{ mobile: 'block', tablet: 'none' }}>
            <Text weight="semibold" size="large">
              {title}
            </Text>
          </Box>
          <Box display={{ mobile: 'none', tablet: 'block' }}>
            <Text weight="semibold" size="xlarge">
              {title}
            </Text>
          </Box>
          {/* Description is dark (not muted), 17px on all screens. */}
          {descriptionItems.length ? (
            <Stack gap="xlarge">
              {descriptionItems.map((item, index) => (
                <Text key={`description-${index}`} size="standard">
                  {item}
                </Text>
              ))}
            </Stack>
          ) : description ? (
            <Text size="standard">{description}</Text>
          ) : null}
        </Stack>
        {callout ? (
          <Box background="infoMuted" padding="large" borderRadius="medium">
            <Text tone="muted">{callout}</Text>
          </Box>
        ) : null}
        {guidanceTitle || guidanceItems.length ? (
          <Box background="infoMuted" padding="large" borderRadius="medium" marginBottom="small">
            <Stack gap="xlarge">
              {guidanceTitle ? (
                <Text weight="semibold" size="standard" tone="neutral">
                  {guidanceTitle}
                </Text>
              ) : null}
              {guidanceItems.length ? (
                <TextList type="bullet" gap="large">
                  {guidanceItems.map(item => (
                    <Text key={item} size="small">
                      {item}
                    </Text>
                  ))}
                </TextList>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
